import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.jsx';
import { useRealtimeBuscas, useUpdateBusca } from '../../hooks/useRealtime';
import { usePWA } from '../../hooks/usePWA';
import { ROUTES, STATUS } from '../../lib/constants';
import Button from '../../components/common/Button';
import StatusBadge from '../../components/common/StatusBadge';
import Card from '../../components/common/Card';
import logoImg from '../../assets/logo.png';
import { Link } from 'react-router-dom';
import StudentList from '../../components/dashboard/StudentList';
import StudentImport from '../../components/dashboard/StudentImport';
import UserManagement from '../../components/dashboard/UserManagement';
import ParentList from '../../components/dashboard/ParentList';
import GateManagement from '../../components/dashboard/GateManagement';

export default function SchoolDashboard() {
    const navigate = useNavigate();
    const { user, profile, loading: profileLoading, signOut, isSchool, isDirector, isMonitor } = useAuth();
    const { buscas, loading: buscasLoading } = useRealtimeBuscas(profile?.escola_id, null);
    const { updateStatus, updating } = useUpdateBusca();
    const { isInstallable, installApp } = usePWA();

    const [activeTab, setActiveTab] = useState('buscas'); // 'buscas', 'alunos', 'importar', 'equipe', 'portoes'
    const [refreshSignal, setRefreshSignal] = useState(0);

    // Redirecionar se não for escola
    useEffect(() => {
        if (!profileLoading && profile && !isSchool) {
            navigate(ROUTES.APP_PARENT, { replace: true });
        }
    }, [profile, profileLoading, isSchool, navigate]);

    const handleLogout = async () => {
        await signOut();
        navigate(ROUTES.HOME);
    };

    const handleUpdateStatus = async (buscaId, novoStatus) => {
        try {
            await updateStatus(buscaId, novoStatus, user?.id);
        } catch (error) {
            console.error('Erro ao atualizar status:', error);
            alert('Erro ao atualizar status. Tente novamente.');
        }
    };

    // Filtrar buscas ativas (não entregues ou canceladas)
    // Filtrar buscas ativas (não entregues ou canceladas)
    // NOVO: Se for monitor, filtra apenas as buscas do seu portão
    const buscasFiltradas = buscas.filter(b => {
        // Diretor vê tudo
        if (isDirector) return true;

        // Se o monitor estiver vinculado a um portão específico
        if (profile?.portao_id) {
            // Se o aluno não tiver portão definido, mostra pro monitor (floater)
            if (!b.aluno?.portao_id) return true;
            // Se tiver, precisa ser o mesmo
            return b.aluno.portao_id === profile.portao_id;
        }

        // Monitor sem portão fixo vê tudo
        return true;
    });

    const buscasAtivas = buscasFiltradas.filter(
        (b) => b.status !== 'entregue' && b.status !== 'cancelado'
    );

    const buscasFinalizadas = buscasFiltradas.filter(
        (b) => b.status === 'entregue' || b.status === 'cancelado'
    );

    const getNextStatus = (currentStatus) => {
        const statusFlow = {
            chegando: 'preparando',
            preparando: 'pronto',
            pronto: 'entregue',
        };
        return statusFlow[currentStatus];
    };

    const getNextStatusLabel = (currentStatus) => {
        const nextStatus = getNextStatus(currentStatus);
        return nextStatus ? STATUS[nextStatus]?.label : '';
    };

    // Se estiver carregando o perfil do AUTH, mostra loading
    if (profileLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-7xl mb-4 animate-bounce">🚗</div>
                    <p className="text-xl text-gray-600 font-black tracking-tighter">FILA ZERO</p>
                    <p className="text-gray-400">Verificando sessao...</p>
                </div>
            </div>
        );
    }

    // Se por algum motivo o dashboard carregar e as buscas estiverem carregando COM perfil pronto
    const showSearchLoading = buscasLoading && activeTab === 'buscas';

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white shadow-sm border-b sticky top-0 z-10">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex justify-between items-center">
                        <Link to={ROUTES.HOME} className="flex items-center gap-3">
                            <img src={logoImg} alt="Fila0 Logo" className="h-16 w-auto object-contain" />
                            <div className="hidden sm:block">
                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest leading-none mb-1">Escola Autorizada</p>
                                <p className="text-sm text-gray-800 font-black leading-tight">
                                    {profile?.nome_completo || 'Dashboard'}
                                </p>
                                <span className="text-[9px] font-black uppercase bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                                    {isDirector ? 'Diretor' : `Monitor • 🚪 ${profile?.portao?.nome || 'Geral'}`}
                                </span>
                            </div>
                        </Link>

                        {/* Tab Switcher Desktop */}
                        <div className="hidden md:flex bg-gray-100 p-1 rounded-xl">
                            <button
                                onClick={() => setActiveTab('buscas')}
                                className={`px-5 py-2.5 rounded-lg transition-all text-xs font-black uppercase tracking-wider ${activeTab === 'buscas' ? 'bg-white shadow-lg text-primary' : 'text-gray-500 hover:text-primary'}`}
                            >
                                🚗 Monitor view
                            </button>

                            {isDirector && (
                                <>
                                    <button
                                        onClick={() => setActiveTab('alunos')}
                                        className={`px-5 py-2.5 rounded-lg transition-all text-xs font-black uppercase tracking-wider ${activeTab === 'alunos' ? 'bg-white shadow-lg text-primary' : 'text-gray-500 hover:text-primary'}`}
                                    >
                                        👨‍🎓 Alunos
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('importar')}
                                        className={`px-5 py-2.5 rounded-lg transition-all text-xs font-black uppercase tracking-wider ${activeTab === 'importar' ? 'bg-white shadow-lg text-primary' : 'text-gray-500 hover:text-primary'}`}
                                    >
                                        📥 Importar
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('equipe')}
                                        className={`px-5 py-2.5 rounded-lg transition-all text-xs font-black uppercase tracking-wider ${activeTab === 'equipe' ? 'bg-white shadow-lg text-primary' : 'text-gray-500 hover:text-primary'}`}
                                    >
                                        🛡️ Equipe
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('portoes')}
                                        className={`px-5 py-2.5 rounded-lg transition-all text-xs font-black uppercase tracking-wider ${activeTab === 'portoes' ? 'bg-white shadow-lg text-primary' : 'text-gray-500 hover:text-primary'}`}
                                    >
                                        🚪 Portões
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('pais')}
                                        className={`px-5 py-2.5 rounded-lg transition-all text-xs font-black uppercase tracking-wider ${activeTab === 'pais' ? 'bg-white shadow-lg text-primary' : 'text-gray-500 hover:text-primary'}`}
                                    >
                                        👨‍👩‍👧‍👦 Pais
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('historico')}
                                        className={`px-5 py-2.5 rounded-lg transition-all text-xs font-black uppercase tracking-wider ${activeTab === 'historico' ? 'bg-white shadow-lg text-primary' : 'text-gray-500 hover:text-primary'}`}
                                    >
                                        📋 Histórico
                                    </button>
                                </>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            {isInstallable && (
                                <button
                                    onClick={installApp}
                                    className="hidden sm:flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-primary/20 transition-all border border-primary/20"
                                >
                                    <span>📱</span> Instalar App
                                </button>
                            )}

                            <button
                                onClick={handleLogout}
                                className="bg-gray-50 hover:bg-red-50 text-gray-400 hover:text-red-500 p-2.5 rounded-xl transition-all border border-gray-100"
                                title="Sair do sistema"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tab Switcher Mobile */}
                {isDirector && (
                    <div className="md:hidden flex bg-white border-t overflow-x-auto whitespace-nowrap scrollbar-hide">
                        <button
                            onClick={() => setActiveTab('buscas')}
                            className={`flex-1 px-6 py-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-colors ${activeTab === 'buscas' ? 'border-primary text-primary' : 'border-transparent text-gray-400'}`}
                        >
                            Monitor
                        </button>
                        <button
                            onClick={() => setActiveTab('alunos')}
                            className={`flex-1 px-6 py-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-colors ${activeTab === 'alunos' ? 'border-primary text-primary' : 'border-transparent text-gray-400'}`}
                        >
                            Alunos
                        </button>
                        <button
                            onClick={() => setActiveTab('importar')}
                            className={`flex-1 px-6 py-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-colors ${activeTab === 'importar' ? 'border-primary text-primary' : 'border-transparent text-gray-400'}`}
                        >
                            Importar
                        </button>
                        <button
                            onClick={() => setActiveTab('equipe')}
                            className={`flex-1 px-6 py-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-colors ${activeTab === 'equipe' ? 'border-primary text-primary' : 'border-transparent text-gray-400'}`}
                        >
                            Equipe
                        </button>
                        <button
                            onClick={() => setActiveTab('portoes')}
                            className={`flex-1 px-6 py-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-colors ${activeTab === 'portoes' ? 'border-primary text-primary' : 'border-transparent text-gray-400'}`}
                        >
                            Portões
                        </button>
                        <button
                            onClick={() => setActiveTab('pais')}
                            className={`flex-1 px-6 py-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-colors ${activeTab === 'pais' ? 'border-primary text-primary' : 'border-transparent text-gray-400'}`}
                        >
                            Pais
                        </button>
                        <button
                            onClick={() => setActiveTab('historico')}
                            className={`flex-1 px-6 py-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-colors ${activeTab === 'historico' ? 'border-primary text-primary' : 'border-transparent text-gray-400'}`}
                        >
                            Histórico
                        </button>
                    </div>
                )}
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 py-8 max-w-7xl">
                {showSearchLoading && (
                    <div className="text-center py-20">
                        <div className="text-4xl animate-spin mb-4 inline-block">⏳</div>
                        <p className="font-bold text-gray-400 uppercase tracking-widest text-xs">Atualizando dados...</p>
                    </div>
                )}

                {!showSearchLoading && activeTab === 'buscas' && (
                    <div className="animate-fade-in">
                        {/* Stats */}
                        <div className={`${isMonitor ? 'hidden sm:grid' : 'grid'} grid-cols-1 sm:grid-cols-3 gap-4 mb-8`}>
                            <Card className="text-center group hover:border-primary transition-colors">
                                <div className="text-3xl font-bold text-primary mb-1">
                                    {buscasAtivas.length}
                                </div>
                                <p className="text-gray-600 text-sm">Buscas em Andamento</p>
                            </Card>
                            <Card className="text-center group hover:border-success transition-colors">
                                <div className="text-3xl font-bold text-success mb-1">
                                    {buscas.filter((b) => b.status === 'entregue').length}
                                </div>
                                <p className="text-gray-600 text-sm">Entregues Hoje</p>
                            </Card>
                            <Card className="text-center group hover:border-gray-400 transition-colors">
                                <div className="text-3xl font-bold text-gray-600 mb-1">
                                    {buscas.length}
                                </div>
                                <p className="text-gray-600 text-sm">Total Hoje</p>
                            </Card>
                        </div>

                        {/* Buscas Ativas */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                                <span className="p-2 bg-primary/10 rounded-lg text-primary">🔔</span> Buscas em Andamento
                            </h2>

                            {buscasAtivas.length === 0 ? (
                                <Card className="text-center py-20 border-2 border-dashed border-gray-200 bg-transparent">
                                    <div className="text-6xl mb-4">✨</div>
                                    <h3 className="text-xl font-semibold mb-2">Tudo tranquilo por enquanto!</h3>
                                    <p className="text-gray-500 max-w-sm mx-auto">
                                        Nenhuma busca ativada pelos pais no momento. Relaxe, a Fila 0 está funcionando!
                                    </p>
                                </Card>
                            ) : (
                                <div className="grid gap-4">
                                    {buscasAtivas.map((busca) => (
                                        <Card key={busca.id} className="hover:shadow-lg transition-all border-l-4" style={{ borderLeftColor: STATUS[busca.status]?.color || '#2666a6' }}>
                                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                                                {/* Info */}
                                                <div className="flex-1">
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div>
                                                            <h3 className="font-bold text-xl text-gray-900 leading-tight">
                                                                {busca.aluno?.nome_completo || 'Aluno'}
                                                            </h3>
                                                            <div className="flex items-center gap-2 mt-1">
                                                                <div className="inline-flex items-center px-2 py-0.5 rounded text-[9px] font-black bg-gray-900 text-white uppercase tracking-wider">
                                                                    Turma: {busca.aluno?.turma || '-'}
                                                                </div>
                                                                <div className="inline-flex items-center px-2 py-0.5 rounded text-[9px] font-black bg-primary/10 text-primary uppercase tracking-wider border border-primary/10">
                                                                    🚪 {busca.aluno?.portao?.nome || 'Geral'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <StatusBadge status={busca.status} />
                                                    </div>

                                                    <div className="grid sm:grid-cols-2 gap-4 text-sm bg-gray-50 p-3 rounded-lg">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-gray-400">👤 Respon.:</span>
                                                            <span className="font-semibold">{busca.responsavel?.nome_completo || '-'}</span>
                                                        </div>
                                                        {busca.responsavel?.telefone && (
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-gray-400">📱 Cel:</span>
                                                                <a href={`tel:${busca.responsavel.telefone}`} className="font-semibold text-primary hover:underline">{busca.responsavel.telefone}</a>
                                                            </div>
                                                        )}
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-gray-400">🕒 Início:</span>
                                                            <span className="font-medium">{new Date(busca.criado_em).toLocaleTimeString('pt-BR')}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Actions */}
                                                <div className="flex flex-col gap-2 md:w-72">
                                                    {busca.status !== 'entregue' && (
                                                        <>
                                                            <Button
                                                                variant="primary"
                                                                onClick={() => handleUpdateStatus(busca.id, getNextStatus(busca.status))}
                                                                loading={updating}
                                                                className="w-full text-lg py-4 shadow-lg shadow-primary/20"
                                                            >
                                                                ✅ Marcar como "{getNextStatusLabel(busca.status)}"
                                                            </Button>

                                                            {busca.status === 'chegando' && (
                                                                <button
                                                                    onClick={() => {
                                                                        if (confirm('Deseja realmente cancelar esta busca?')) {
                                                                            handleUpdateStatus(busca.id, 'cancelado');
                                                                        }
                                                                    }}
                                                                    className="text-sm text-gray-400 hover:text-red-500 py-2 transition-colors"
                                                                    disabled={updating}
                                                                >
                                                                    ❌ Cancelar busca
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Stats Mobile (Monitor only) */}
                        {isMonitor && (
                            <div className="grid grid-cols-1 gap-4 mb-8 sm:hidden">
                                <Card className="text-center group hover:border-primary transition-colors">
                                    <div className="text-3xl font-bold text-primary mb-1">
                                        {buscasAtivas.length}
                                    </div>
                                    <p className="text-gray-600 text-sm">Buscas em Andamento</p>
                                </Card>
                                <Card className="text-center group hover:border-success transition-colors">
                                    <div className="text-3xl font-bold text-success mb-1">
                                        {buscas.filter((b) => b.status === 'entregue').length}
                                    </div>
                                    <p className="text-gray-600 text-sm">Entregues Hoje</p>
                                </Card>
                                <Card className="text-center group hover:border-gray-400 transition-colors">
                                    <div className="text-3xl font-bold text-gray-600 mb-1">
                                        {buscas.length}
                                    </div>
                                    <p className="text-gray-600 text-sm">Total Hoje</p>
                                </Card>
                            </div>
                        )}

                        {/* Histórico */}
                        {buscasFinalizadas.length > 0 && (
                            <div className="animate-fade-in delay-200">
                                <h2 className="text-xl font-bold mb-6 flex items-center gap-2 text-gray-600">
                                    <span className="p-1.5 bg-gray-200 rounded text-gray-500">📋</span> Finalizados Hoje
                                </h2>
                                <div className="grid gap-3 opacity-80">
                                    {buscasFinalizadas.slice(0, 10).map((busca) => (
                                        <Card key={busca.id} className="bg-white/60 border-gray-100">
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <p className="font-semibold text-gray-800">
                                                        {busca.aluno?.nome_completo || 'Aluno'}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {new Date(busca.criado_em).toLocaleTimeString('pt-BR')} →{' '}
                                                        {busca.entregue_em
                                                            ? new Date(busca.entregue_em).toLocaleTimeString('pt-BR')
                                                            : '-'}
                                                    </p>
                                                </div>
                                                <StatusBadge status={busca.status} showIcon={false} />
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'alunos' && (
                    <div className="animate-fade-in">
                        <StudentList
                            escolaId={profile?.escola_id}
                            refreshSignal={refreshSignal}
                        />
                    </div>
                )}

                {activeTab === 'importar' && (
                    <div className="max-w-2xl mx-auto animate-fade-in">
                        <StudentImport
                            escolaId={profile?.escola_id}
                            onComplete={() => {
                                setRefreshSignal(s => s + 1);
                                setTimeout(() => setActiveTab('alunos'), 2000);
                            }}
                        />
                    </div>
                )}
                {activeTab === 'equipe' && (
                    <div className="animate-fade-in">
                        <UserManagement escolaId={profile?.escola_id} />
                    </div>
                )}
                {activeTab === 'portoes' && (
                    <div className="animate-fade-in">
                        <GateManagement escolaId={profile?.escola_id} />
                    </div>
                )}
                {activeTab === 'pais' && (
                    <div className="animate-fade-in">
                        <ParentList escolaId={profile?.escola_id} />
                    </div>
                )}
                {activeTab === 'historico' && (
                    <div className="animate-fade-in space-y-6">
                        <div className="bg-white p-6 rounded-3xl shadow-sm border">
                            <h2 className="text-2xl font-black text-gray-900 tracking-tighter">Histórico de Saídas</h2>
                            <p className="text-gray-500 text-sm font-medium">Todos os alunos que já foram entregues ou cancelados hoje</p>
                        </div>

                        {buscasFinalizadas.length === 0 ? (
                            <Card className="text-center py-20 border-2 border-dashed border-gray-200">
                                <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">Nenhum registro finalizado hoje.</p>
                            </Card>
                        ) : (
                            <div className="grid gap-3">
                                {buscasFinalizadas.map((busca) => (
                                    <Card key={busca.id} className="bg-white border-gray-100 flex items-center justify-between group hover:border-gray-300 transition-all">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-lg">
                                                {busca.status === 'entregue' ? '✅' : '❌'}
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-800 leading-tight">
                                                    {busca.aluno?.nome_completo || 'Aluno'}
                                                </p>
                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                                                    {new Date(busca.criado_em).toLocaleTimeString('pt-BR')} → {busca.entregue_em ? new Date(busca.entregue_em).toLocaleTimeString('pt-BR') : 'Cancelado'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="hidden sm:block text-right">
                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Responsável</p>
                                                <p className="text-xs font-bold text-gray-600">{busca.responsavel?.nome_completo?.split(' ')[0]}</p>
                                            </div>
                                            <StatusBadge status={busca.status} showIcon={false} />
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}
